import { useEffect, useRef } from 'react';
import './LowKickAnimation.css';

export default function LowKickAnimation({ playerRef, opponentRef, onComplete }) {
  const animRef = useRef(null);
  
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    // Get battle container
    const container = playerEl.closest('.battle-container');
    if (!container) {
      console.error('Battle container not found');
      onComplete();
      return;
    }

    // Get positions for opponent
    const o = opponentEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Calculate center of opponent sprite (targeting lower area for low kick)
    const frameWidth = 192;  // Width of each frame (384px total ÷ 2 frames)
    const frameHeight = 192; // Height of the sprite
    const centerX = o.left - containerRect.left + o.width / 2;
    const centerY = o.top - containerRect.top + o.height * 0.8; // Target lower portion

    // Initial player animation - crouching preparation
    playerEl.style.transition = 'transform 0.25s ease-in-out, filter 0.2s ease-in-out';
    playerEl.style.transform = 'scale(0.95) translateY(5px)'; // Slight crouch
    playerEl.style.filter = 'brightness(1.1)';
    
    // Create and position animation element
    const anim = document.createElement('div');
    anim.className = 'low-kick-anim';
    anim.style.setProperty('--sx', `${centerX - frameWidth / 2}px`);
    anim.style.setProperty('--sy', `${centerY - frameHeight / 2}px`);
    container.appendChild(anim);
    animRef.current = anim;
    
    // Quick dash forward for the kick
    const dashTimer = setTimeout(() => {
      playerEl.style.transform = 'scale(1.05) translateY(-3px)'; // Quick forward motion
    }, 150);
    
    // Create kick effects at impact moment
    const kickEffectTimer = setTimeout(() => {
      // Impact flash with fighting-type colors
      const flash = document.createElement('div');
      flash.className = 'low-kick-flash';
      flash.style.left = `${centerX}px`;
      flash.style.top = `${centerY}px`;
      container.appendChild(flash);
      
      // Create dust particles from the ground impact
      for (let i = 0; i < 8; i++) {
        const particle = document.createElement('div');
        particle.className = 'low-kick-dust';
        
        const angle = Math.PI + (Math.random() - 0.5) * Math.PI; // Upward arc
        const distance = 25 + Math.random() * 20;
        const size = 4 + Math.random() * 4;
        
        particle.style.left = `${centerX}px`;
        particle.style.top = `${centerY + 10}px`; // Slightly below impact point
        particle.style.setProperty('--size', `${size}px`);
        particle.style.setProperty('--angle', `${angle}rad`);
        particle.style.setProperty('--distance', `${distance}px`);
        particle.style.setProperty('--rotation', `${Math.random() * 360}deg`);
        
        container.appendChild(particle);
        
        // Remove particle after animation
        setTimeout(() => {
          if (particle.parentNode) {
            particle.parentNode.removeChild(particle);
          }
        }, 600);
      }
      
      // Create impact lines for dramatic effect
      for (let i = 0; i < 4; i++) {
        const line = document.createElement('div');
        line.className = 'low-kick-impact-line';
        
        const angle = (i / 4) * Math.PI * 2;
        const length = 30 + Math.random() * 15;
        
        line.style.left = `${centerX}px`;
        line.style.top = `${centerY}px`;
        line.style.setProperty('--angle', `${angle}rad`);
        line.style.setProperty('--length', `${length}px`);
        
        container.appendChild(line);
        
        // Remove line after animation
        setTimeout(() => {
          if (line.parentNode) {
            line.parentNode.removeChild(line);
          }
        }, 400);
      }
      
      // Remove flash effect
      setTimeout(() => {
        if (flash.parentNode) flash.parentNode.removeChild(flash);
      }, 300);
      
    }, 300); // Timing for impact
    
    // Opponent hit reaction - stumbling from low kick
    const opponentHitTimer = setTimeout(() => {
      opponentEl.style.transition = 'transform 0.2s ease-out, filter 0.15s ease-out';
      opponentEl.style.transform = 'scale(0.98) translateX(-6px) translateY(3px) rotate(-2deg)';
      opponentEl.style.filter = 'brightness(1.3) saturate(1.2)';
      
      // Secondary stumble motion
      setTimeout(() => {
        opponentEl.style.transform = 'scale(1.01) translateX(3px) translateY(-1px) rotate(1deg)';
      }, 200);
      
      // Return to normal
      setTimeout(() => {
        opponentEl.style.transform = 'scale(1)';
        opponentEl.style.filter = '';
      }, 400);
    }, 320);
    
    // Player recovery animation
    const recoveryTimer = setTimeout(() => {
      playerEl.style.transform = 'scale(1)';
      playerEl.style.filter = '';
    }, 500);
    
    // Complete animation and cleanup
    const completeTimer = setTimeout(() => {
      // Remove main animation element
      if (animRef.current && animRef.current.parentNode) {
        animRef.current.parentNode.removeChild(animRef.current);
      }
      
      onComplete();
    }, 800); // Total animation duration
    
    // Cleanup function
    return () => {
      clearTimeout(dashTimer);
      clearTimeout(kickEffectTimer);
      clearTimeout(opponentHitTimer);
      clearTimeout(recoveryTimer);
      clearTimeout(completeTimer);
      
      // Reset styles
      playerEl.style.transform = '';
      playerEl.style.filter = '';
      opponentEl.style.transform = '';
      opponentEl.style.filter = '';
      
      // Remove animation element
      if (animRef.current && animRef.current.parentNode) {
        animRef.current.parentNode.removeChild(animRef.current);
      }
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}
